/**
 * OperationCard.js
 *
 * FT-161 Phase 4: Consolidated operation card for active voyage log
 * Groups operation entries (section, data, checks, complete) into a single card.
 *
 * Handles ALL operation types dynamically with custom fields.
 */

import React, { useState } from 'react';
import {
  Users,
  Anchor,
  Ship,
  Eye,
  Clock,
  Check,
  ChevronDown,
  ChevronUp,
  Activity,
  AlertTriangle
} from 'lucide-react';
import { OPERATION_TYPE_LABELS, getOperationTemplate } from '../../../../../config/operationsConfig';
import { useStorageState } from '../../../../../hooks/useStorageState';
import { formatDateInTimezone } from '../../../../../utils/timezoneUtils';

// Standard system fields to exclude from custom fields display
// These are internal/metadata fields, not user-entered operation data
const STANDARD_FIELDS = [
  // Identity fields
  'id', 'type', 'operationId', 'operationType', 'vesselId', 'voyageId',
  // Timestamps
  'timestamp', 'createdAt', 'updatedAt', 'syncedAt', 'completedAt',
  // Attribution
  'createdBy', 'updatedBy', 'completedBy',
  // Sync/system fields
  'locked', 'synced', 'syncId', 'syncStatus', 'localId', 'serverId',
  // Other metadata
  'version', '_rev', '_id'
];

// Check if a value looks like user-entered data (not a system ID or timestamp)
const isDisplayableValue = (value) => {
  // Null/undefined/empty - not displayable
  if (value === null || value === undefined || value === '') return false;

  // Boolean - displayable
  if (typeof value === 'boolean') return true;

  // String - displayable (most user data)
  if (typeof value === 'string') return true;

  // Number - check if it looks like a timestamp (13+ digits) vs normal number
  if (typeof value === 'number') {
    // If it's a large number that looks like a timestamp, skip it
    if (value > 1000000000000) return false; // Millisecond timestamp
    if (value > 1000000000 && value < 2000000000) return false; // Second timestamp
    return true; // Normal numbers are displayable
  }

  // Arrays/objects - skip
  if (typeof value === 'object') return false;

  return true;
};

// Check if a string looks like a generated ID (timestamp + random chars)
// e.g., "1776161743893-FO0LXY7G5" or "abc123xyz"
const looksLikeGeneratedId = (str) => {
  if (typeof str !== 'string') return false;
  // Pattern: starts with many digits (timestamp) followed by random chars
  if (/^\d{10,}-[A-Z0-9]+$/i.test(str)) return true;
  // Pattern: long string with mix of numbers and random chars, no spaces
  if (str.length > 15 && !/\s/.test(str) && /\d/.test(str) && /[A-Z]/i.test(str)) return true;
  return false;
};

// Icon mapping for operation types
const OPERATION_ICONS = {
  crew_transfer: Users,
  pilot_transfer: Users,
  towing: Ship,
  anchoring: Anchor,
  standby: Clock,
  diving_support: Activity,
  restricted_visibility: Eye,
  arrival: Anchor,
  default: Activity
};

const OperationCard = ({
  operationType,
  sectionEntry,
  operationDataEntry,
  checks = [],
  completeEntry,
  vesselTimezone = 'UTC'  // FT-285 §13.5: for skipped-check attribution timestamp
}) => {
  const [isChecklistExpanded, setIsChecklistExpanded] = useState(false);

  // Load voyage settings to look up field labels from template
  const { value: voyageSettings } = useStorageState('voyage_settings', null);

  // Get operation template to look up field labels
  const template = getOperationTemplate(operationType, voyageSettings);
  const templateFields = template?.fields || [];

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  // Format operation type for display
  const formatOperationType = (type) => {
    // Use config labels first, fallback to formatting
    if (OPERATION_TYPE_LABELS[type]) {
      return OPERATION_TYPE_LABELS[type];
    }
    return (type || 'Operation')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Look up field label from template, fallback to formatting the key
  const getFieldLabel = (fieldId) => {
    // First try to find the field in the template
    const templateField = templateFields.find(f => f.id === fieldId);
    if (templateField?.label) {
      // Found a template label - but verify it's not an ID-like string
      if (!looksLikeGeneratedId(templateField.label)) {
        return templateField.label;
      }
    }
    // Fallback: format the field ID itself
    // But only if the fieldId itself doesn't look like a generated ID
    if (looksLikeGeneratedId(fieldId)) {
      return null; // Signal to skip this field
    }
    return fieldId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Extract custom fields from operation_data
  // Only include user-entered fields with displayable values and valid labels
  const getCustomFields = () => {
    if (!operationDataEntry) return [];
    return Object.entries(operationDataEntry)
      .filter(([k, v]) =>
        !STANDARD_FIELDS.includes(k) &&
        isDisplayableValue(v)
      )
      .map(([k, v]) => {
        const label = getFieldLabel(k);
        return label ? { key: k, label, value: v } : null;
      })
      .filter(Boolean); // Remove any null entries (fields with ID-like labels)
  };

  // Calculate duration
  const calculateDuration = () => {
    if (!sectionEntry?.timestamp) return null;
    const startTime = new Date(sectionEntry.timestamp);
    const endTime = completeEntry?.timestamp
      ? new Date(completeEntry.timestamp)
      : new Date();
    const durationMs = endTime - startTime;
    const durationMins = Math.round(durationMs / 60000);

    if (durationMins < 60) {
      return `${durationMins} min`;
    }
    const hours = Math.floor(durationMins / 60);
    const mins = durationMins % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Get status
  const isComplete = !!completeEntry;
  const statusText = isComplete ? 'Complete' : 'In Progress';

  // Get completedBy name
  const getCompletedBy = () => {
    if (completeEntry?.createdBy?.name) return completeEntry.createdBy.name;
    if (completeEntry?.createdBy) return completeEntry.createdBy;
    if (sectionEntry?.createdBy?.name) return sectionEntry.createdBy.name;
    if (sectionEntry?.createdBy) return sectionEntry.createdBy;
    return 'Unknown';
  };

  // Get icon component
  const IconComponent = OPERATION_ICONS[operationType] || OPERATION_ICONS.default;

  // Get custom fields
  const customFields = getCustomFields();

  // Checklist stats
  const completedChecks = checks.filter(c => c.completed).length;
  const skippedChecks = checks.filter(c => c.skipped).length;
  const totalChecks = checks.length;
  const hasChecklist = totalChecks > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border-l-4 border-teal-600 p-4 mx-4 my-2">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <IconComponent className="h-5 w-5 text-teal-600" />
          <span className="text-base font-semibold text-gray-900">
            {formatOperationType(operationType)}
          </span>
        </div>
        <span className="text-sm text-gray-400 font-mono">
          {formatTime(sectionEntry?.timestamp)}
        </span>
      </div>

      {/* Custom Fields Grid (if any) */}
      {customFields.length > 0 && (
        <div className={`grid gap-4 py-2 mb-2 ${customFields.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {customFields.map((field) => (
            <div key={field.key}>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                {field.label}
              </p>
              <p className="text-sm font-medium text-gray-900">
                {String(field.value)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Duration and Status Grid */}
      <div className="grid grid-cols-2 gap-4 py-2 border-t border-gray-100">
        {/* Duration */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            Duration
          </p>
          <p className="text-sm font-medium text-gray-900">
            {calculateDuration() || '---'}
          </p>
        </div>

        {/* Status */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            Status
          </p>
          <div className="flex items-center gap-1.5">
            {isComplete ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium text-green-600">{statusText}</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-amber-600">{statusText}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Checklist Section (if has checks) */}
      {hasChecklist && (
        <div className="border-t border-gray-100 mt-2 pt-2">
          {/* Checklist Header - Clickable */}
          <button
            onClick={() => setIsChecklistExpanded(!isChecklistExpanded)}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="text-sm font-medium text-gray-700">
              Checklist ({completedChecks + skippedChecks}/{totalChecks})
            </span>
            {isChecklistExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {/* Checklist Items */}
          {isChecklistExpanded && (
            <div className="space-y-1.5 mt-1">
              {checks.map((check, index) => {
                // FT-285 §13.5 (LOCKED): universal skipped treatment.
                // Reads both V2 (skipReason) and V1 (skippedReason) field names.
                if (check.skipped) {
                  const skipReason = check.skipReason || check.skippedReason;
                  const skippedTimestamp = check.skippedAt || check.completedAt;
                  let skippedTimeLabel = '';
                  if (skippedTimestamp) {
                    try {
                      skippedTimeLabel = formatDateInTimezone(skippedTimestamp, vesselTimezone, { format: 'time' });
                    } catch {
                      skippedTimeLabel = formatTime(skippedTimestamp);
                    }
                  }
                  return (
                    <div
                      key={check.checkId || check.id || index}
                      className="bg-amber-50 border border-amber-200 rounded-md p-3"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {check.text || check.label}
                            </span>
                            <span className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
                              Skipped
                            </span>
                          </div>
                          {skipReason && (
                            <p className="text-sm italic text-amber-800 mt-1">
                              {skipReason}
                            </p>
                          )}
                          {check.skippedBy && (
                            <p className="text-xs text-amber-700 mt-1">
                              Skipped by {check.skippedBy}
                              {skippedTimeLabel && ` at ${skippedTimeLabel}`}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={check.checkId || check.id || index}
                    className="flex items-center gap-2 py-1"
                  >
                    {check.completed ? (
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 border-2 border-gray-300 rounded flex-shrink-0" />
                    )}
                    <span className={`text-sm flex-1 ${
                      check.completed ? 'text-gray-700' : 'text-gray-500'
                    }`}>
                      {check.text || check.label}
                    </span>
                    {check.completed && check.completedAt && (
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {formatTime(check.completedAt)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-400">
          {isComplete ? 'Completed by: ' : 'Started by: '}{getCompletedBy()}
        </span>
      </div>
    </div>
  );
};

export default OperationCard;
